/**
 * Runtime detection for upgrade routing (RevenueCat on native iOS, Stripe elsewhere).
 *
 * Installed PWA / ordinary Safari must never take the StoreKit path — even when
 * stale Capacitor JS, plugins, or OAuth session leftovers are present.
 */

import { isCapacitorIosApp, isCapacitorAndroidApp } from '@/lib/auth/capacitorSignIn'
import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'
import { isStandalone } from '@/lib/pwa/isStandalone'

type CapacitorBridge = {
  isNativePlatform?: () => boolean
  isNative?: boolean
  getPlatform?: () => string
  getServerUrl?: () => string
  Plugins?: Record<string, unknown>
}

function readCapacitorBridge(): CapacitorBridge | null {
  if (typeof window === 'undefined') return null
  return (window as Window & { Capacitor?: CapacitorBridge }).Capacitor ?? null
}

function isIosUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

/**
 * True when upgrade must use RevenueCat / StoreKit (never Stripe Checkout).
 * Strict: requires a real native Capacitor shell. Standalone PWA → false.
 */
export function detectNativeIosForUpgrade(): boolean {
  const standalone =
    typeof window !== 'undefined' ? isStandalone() : false
  const isNativeCap = isNativeCapacitorApp()
  const isCapIos = isCapacitorIosApp()
  const cap = readCapacitorBridge()
  const platform = cap?.getPlatform?.() ?? null

  // Installed PWA is Stripe-only — ignore Cap leftovers / plugin ghosts.
  if (standalone && !isNativeCap) {
    console.log('[UpgradeTrace9419bd7] detectNativeIosForUpgrade', {
      result: false,
      reason: 'standalone_pwa_stripe_only',
      isStandalone: standalone,
      isNativeCapacitorApp: isNativeCap,
      isCapacitorIosApp: isCapIos,
      capacitorPlatform: platform,
    })
    return false
  }

  // Non-native web (Safari tab, desktop, Android browser): Stripe.
  // Do not trust getPlatform()/Plugins alone — Cap JS can linger without a shell.
  if (!isNativeCap) {
    console.log('[UpgradeTrace9419bd7] detectNativeIosForUpgrade', {
      result: false,
      reason: 'not_native_capacitor',
      isStandalone: standalone,
      isNativeCapacitorApp: isNativeCap,
      isCapacitorIosApp: isCapIos,
      capacitorPlatform: platform,
      hasCapBridge: Boolean(cap),
      pluginKeys: cap?.Plugins ? Object.keys(cap.Plugins) : [],
    })
    return false
  }

  if (isCapacitorAndroidApp()) {
    console.log('[UpgradeTrace9419bd7] detectNativeIosForUpgrade', {
      result: false,
      reason: 'native_android',
      isNativeCapacitorApp: isNativeCap,
      capacitorPlatform: platform,
    })
    return false
  }

  if (isCapIos) {
    console.log('[UpgradeTrace9419bd7] detectNativeIosForUpgrade', {
      result: true,
      reason: 'isCapacitorIosApp',
      isNativeCapacitorApp: isNativeCap,
      capacitorPlatform: platform,
    })
    return true
  }

  // Native Cap shell with iOS UA (Simulator / remote-server edge cases).
  if (isIosUserAgent() && platform !== 'android') {
    console.log('[UpgradeTrace9419bd7] detectNativeIosForUpgrade', {
      result: true,
      reason: 'native_capacitor_ios_ua',
      isNativeCapacitorApp: isNativeCap,
      capacitorPlatform: platform,
    })
    return true
  }

  console.log('[UpgradeTrace9419bd7] detectNativeIosForUpgrade', {
    result: false,
    reason: 'native_but_not_ios',
    isNativeCapacitorApp: isNativeCap,
    capacitorPlatform: platform,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  })
  return false
}

export function getUpgradePlatformDebugInfo(): Record<string, unknown> {
  const cap = readCapacitorBridge()
  const info = {
    detectNativeIosForUpgrade: detectNativeIosForUpgrade(),
    isCapacitorIosApp: isCapacitorIosApp(),
    isNativeCapacitorApp: isNativeCapacitorApp(),
    isStandalone: typeof window !== 'undefined' ? isStandalone() : false,
    capacitorPlatform: cap?.getPlatform?.() ?? null,
    capacitorServerUrl: (() => {
      try {
        return cap?.getServerUrl?.() ?? null
      } catch {
        return null
      }
    })(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    href: typeof window !== 'undefined' ? window.location.href : null,
  }
  console.log('[UpgradeTrace9419bd7] getUpgradePlatformDebugInfo', info)
  return info
}
